import {FinancingRateMatrixCell, SaveSaleVehicleFinancingBody} from '@dms/api/vehicle';

import {FinancingTemplateDraft} from '../types/FinancingTemplateDraft';
import {applyFinancingTemplate} from './applyFinancingTemplate';
import {areDecimalValuesEqual} from './areDecimalValuesEqual';
import {ensureActiveFinancingCell} from './ensureActiveFinancingCell';

function hasSameCoordinates(left: FinancingRateMatrixCell, right: FinancingRateMatrixCell) {
  return (
    left.term === right.term &&
    left.downPayment === right.downPayment &&
    left.balloonPayment === right.balloonPayment
  );
}

/**
 * Applies a template edit to a body that already follows that template, keeping the edits the user
 * made locally. A body following a different template (or none) is replaced outright.
 */
export function rebaseFinancingTemplate(
  updatedTemplate: FinancingTemplateDraft,
  previousTemplate: FinancingTemplateDraft,
  currentBody: SaveSaleVehicleFinancingBody
) {
  if (
    currentBody.mode !== 'TEMPLATE' ||
    currentBody.templateId !== updatedTemplate.id ||
    previousTemplate.id !== updatedTemplate.id ||
    currentBody.type !== updatedTemplate.type
  ) {
    return applyFinancingTemplate(updatedTemplate, currentBody);
  }

  const manualCells = currentBody.rateMatrix.cells.filter((currentCell) => {
    const previousCell = previousTemplate.rateMatrix.cells.find((cell) =>
      hasSameCoordinates(cell, currentCell)
    );

    return (
      !previousCell ||
      !areDecimalValuesEqual(previousCell.nominalRate, currentCell.nominalRate) ||
      !areDecimalValuesEqual(previousCell.apr, currentCell.apr)
    );
  });
  // The rebase applies the template diff on top of the matrix the user sees, so a coordinate the
  // previous template carried and the body no longer has counts as removed by the user. It stays
  // out even when the template edit changes that coordinate: a local edit already outranks a
  // template edit (see `manualCells`), and a local removal is the same kind of decision. Only
  // coordinates the updated template adds — absent from the previous template — enter the matrix.
  const removedTemplateCells = previousTemplate.rateMatrix.cells.filter(
    (previousCell) =>
      !currentBody.rateMatrix.cells.some((currentCell) =>
        hasSameCoordinates(previousCell, currentCell)
      )
  );
  const rebasedCells = [
    ...updatedTemplate.rateMatrix.cells.filter(
      (templateCell) =>
        !manualCells.some((manualCell) => hasSameCoordinates(templateCell, manualCell)) &&
        !removedTemplateCells.some((removedCell) => hasSameCoordinates(templateCell, removedCell))
    ),
    ...manualCells,
  ];
  const hasCurrentActiveCell = rebasedCells.some(
    (cell) =>
      cell.term === currentBody.activeTerm &&
      cell.downPayment === currentBody.activeDownPayment &&
      cell.balloonPayment === currentBody.activeBalloonPayment
  );
  const templateDefaults = applyFinancingTemplate(updatedTemplate, currentBody);

  // The template default can point at a coordinate the user removed, so the active tuple must be
  // re-established on a surviving cell. An empty matrix keeps the template link and no active tuple.
  return ensureActiveFinancingCell({
    ...currentBody,
    type: updatedTemplate.type,
    mode: 'TEMPLATE',
    templateId: updatedTemplate.id,
    rateMatrix: {
      availableTerms: Array.from(
        new Set([
          ...updatedTemplate.rateMatrix.availableTerms,
          ...manualCells.map((cell) => cell.term),
        ])
      ).sort((left, right) => left - right),
      cells: rebasedCells,
    },
    activeTerm: hasCurrentActiveCell ? currentBody.activeTerm : templateDefaults.activeTerm,
    activeDownPayment: hasCurrentActiveCell
      ? currentBody.activeDownPayment
      : templateDefaults.activeDownPayment,
    activeBalloonPayment: hasCurrentActiveCell
      ? currentBody.activeBalloonPayment
      : templateDefaults.activeBalloonPayment,
  });
}
